import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';
import { AppDispatch, RootState } from '../store';
import { removeConversationIdCookie } from './publicProjectSlice';

// Add this type definition at the top of the file
type JourneyItem = {
  id?: string;
  title: string;
  statement?: string;
  call_to_action?: string;
  page_type: 'prompt';
  position: number;
};

interface MediaItem {
  id: string;
  src: string;
  type: 'image' | 'video';
}

export const fetchFormPageSettings = createAsyncThunk(
  'formPage/fetchFormPageSettings',
  async (
    {
      projectIdOrSlug,
      formPageId,
    }: { projectIdOrSlug: string | number; formPageId: string | number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectIdOrSlug}/form-pages/${formPageId}/settings/get`
      );
      return response.data.settings;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const updateFormPageSettings = createAsyncThunk(
  'formPage/updateFormPageSettings',
  async (
    {
      projectIdOrSlug,
      formPageId,
      settings,
    }: {
      projectIdOrSlug: string | number;
      formPageId: string | number;
      settings: any[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/projects/${projectIdOrSlug}/form-pages/${formPageId}/settings/post`,
        { settings }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// /projects/{projectIdOrSlug}/form-pages/{formPage}/files/{formPageFile}
export const removeBackgroundImage = createAsyncThunk(
  'formPage/removeBackgroundImage',
  async (
    {
      projectId,
      formPageId,
      formPageFileId,
    }: {
      projectId: string | number;
      formPageId: string;
      formPageFileId: number | string;
    },
    { rejectWithValue }
  ) => {
    try {
      await axiosInstance.delete(
        `/projects/${projectId}/form-pages/${formPageId}/files/${formPageFileId}`
      );
      return { projectId, formPageId };
    } catch (error: any) {
      console.error('Error removing background image:', error.response?.data);
      return rejectWithValue(
        error.response?.data || 'Failed to remove background image'
      );
    }
  }
);

export const fetchFormPage = createAsyncThunk(
  'formPage/fetchFormPage',
  async (
    {
      projectId,
      type,
    }: { projectId: string | number; type: 'welcome' | 'end' },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectId}/form-pages/${type}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const updateFormPage = createAsyncThunk(
  'formPage/updateFormPage',
  async (
    { projectIdOrSlug, data }: { projectIdOrSlug: string | number; data: any },
    { rejectWithValue }
  ) => {
    try {
      const cleanedData = Object.fromEntries(
        Object.entries(data).filter(([_, v]) => v !== '')
      );
      const response = await axiosInstance.put(
        `/projects/${projectIdOrSlug}/form-pages/${data?.id}`,
        cleanedData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchAllFormPages = createAsyncThunk(
  'formPage/fetchAllFormPages',
  async (projectId: string | number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectId}/form-pages`
      );
      return response.data.form_pages;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchProjectSettings = createAsyncThunk(
  'formPage/fetchProjectSettings',
  async (projectId: string | number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectId}/settings`
      );
      return response.data.settings;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const updateProjectSettings = createAsyncThunk(
  'formPage/updateProjectSettings',
  async (
    { projectId, data }: { projectId: number | string; data: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.put(
        `/projects/${projectId}/settings`,
        data
      );
      return response.data.settings;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const createFormPage = createAsyncThunk(
  'formPage/createFormPage',
  async (
    {
      projectIdOrSlug,
      data,
    }: { projectIdOrSlug: string | number; data: Partial<JourneyItem> },
    { rejectWithValue }
  ) => {
    try {
      const cleanedData = Object.fromEntries(
        Object.entries(data).filter(([_, v]) => v !== '')
      );
      const response = await axiosInstance.post(
        `/projects/${projectIdOrSlug}/form-pages`,
        cleanedData
      );
      return response.data.form_page;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const deleteFormPage = createAsyncThunk(
  'formPage/deleteFormPage',
  async (
    {
      projectIdOrSlug,
      formPageId,
    }: { projectIdOrSlug: string | number; formPageId: string },
    { rejectWithValue }
  ) => {
    try {
      await axiosInstance.delete(
        `/projects/${projectIdOrSlug}/form-pages/${formPageId}`
      );
      return formPageId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// Add this new async thunk after the existing ones
export const searchPixabayImages = createAsyncThunk(
  'formPage/searchPixabayImages',
  async (
    { searchTerm, perPage = 10 }: { searchTerm: string; perPage?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get('/search-pixabay-images', {
        params: { search_term: searchTerm, per_page: perPage },
      });
      return response.data.medias;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const updateFormPageFiles = createAsyncThunk(
  'formPage/updateFormPageFiles',
  async (
    {
      projectIdOrSlug,
      formPageId,
      formData,
    }: {
      projectIdOrSlug: string | number;
      formPageId: string;
      formData: FormData;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/projects/${projectIdOrSlug}/form-pages/${formPageId}/files/save`,
        formData
      );

      return response.data;
    } catch (error: any) {
      console.error('Error in updateFormPageFiles:', error.response?.data);
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// Add this new async thunk after the existing ones
export const generateFormPageQuestion = createAsyncThunk(
  'formPage/generateFormPageQuestion',
  async (
    {
      projectIdOrSlug,
      formPageId,
      conversationUuid,
      userIdentifier,
      answer,
      file,
    }: {
      projectIdOrSlug: string | number;
      formPageId: string | number | undefined;
      conversationUuid: string;
      userIdentifier: string;
      answer?: any;
      file?: File | null;
    },
    { rejectWithValue }
  ) => {
    try {
      console.log({projectIdOrSlug, formPageId, conversationUuid, userIdentifier, answer, file});
      let response;
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('conversation_uuid', conversationUuid);
        formData.append('user_identifier', userIdentifier);
        if (answer) {
          formData.append('answer', answer.toString());
        }
        response = await axiosInstance.post(
          `/projects/${projectIdOrSlug}/form-pages/generate-question/${formPageId}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
      } else {
        response = await axiosInstance.post(
          `/projects/${projectIdOrSlug}/form-pages/generate-question/${formPageId}`,
          {
            conversation_uuid: conversationUuid,
            user_identifier: userIdentifier,
            answer,
          }
        );
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// Add this new async thunk after the existing ones
export const fetchProjectMedia = createAsyncThunk(
  'formPage/fetchProjectMedia',
  async (projectIdOrSlug: string | number | undefined, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectIdOrSlug}/media`
      );
      return response.data.media;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// Add this new async thunk after the existing ones
export const uploadProjectMedia = createAsyncThunk(
  'formPage/uploadProjectMedia',
  async (
    {
      projectIdOrSlug,
      formData,
    }: {
      projectIdOrSlug: string | number | undefined;
      formData: FormData;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/projects/${projectIdOrSlug}/media`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data.media;
    } catch (error: any) {
      console.error('Error in uploadProjectMedia:', error.response?.data);
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// Add this new async thunk after the existing ones
export const deleteProjectMedia = createAsyncThunk(
  'formPage/deleteProjectMedia',
  async (
    {
      projectIdOrSlug,
      mediaId,
    }: {
      projectIdOrSlug: string | number | undefined;
      mediaId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      await axiosInstance.delete(
        `/projects/${projectIdOrSlug}/media/${mediaId}`
      );
      return mediaId;
    } catch (error: any) {
      console.error('Error in deleteProjectMedia:', error.response?.data);
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// Update the FormPageState interface
interface FormPageState {
  formPages: any[];
  projectSettings: any;
  isLoading: boolean;
  generateFormPageQuestionIsLoading: boolean;
  error: string | null;
  pixabayImages: any[];
  mediaItems: MediaItem[];
  currentFormPageSettings: any;
  generatedQuestion: string | null;
  generatedQuestionOptions: string[] | null;
  answerType: 
    | 'text' 
    | 'multiple_choice' 
    | 'file_upload' 
    | 'nps' 
    | 'date_picker' 
    | 'schedule_meetings' 
    | 'rank_items' 
    | 'youtube_video_link' 
    | 'sign_signature' 
    | 'multiple_choice_selections' 
    | null;
  answerTypeValue: {
    key: string;
    value: string;
  } | null;
  options: string[] | null;
  correctIndex: number | null;
  conversationUuid: string | null;
  isRequired?: boolean;
  callToActionUrl: string | null;
  callToActionTarget: string | null;
  projectMedia: any[];
  project_type?: string | null;
  isConversationCompleted?: boolean;
  isRephrasedQuestion?: boolean;
  isFromLogicFlow?: boolean;
  isFromLogicTemplate?: boolean;
  clarityExplanation?: string | null;
  userDetails?: Record<string, any> | null;
  formPageProjectSettings?: Record<string, any> | null;
  respondentInsights?: {
    completion_rate: string;
    survival_rate: string;
    time_insight: string;
    progress_relative: string;
    real_time_feeling: string;
  } | null;
  moveToFormPageId?: string | null;
}

// Update the initial state
const initialState: FormPageState = {
  formPages: [],
  generateFormPageQuestionIsLoading: false,
  projectSettings: null,
  formPageProjectSettings: null,
  isLoading: false,
  error: null,
  pixabayImages: [],
  mediaItems: [],
  currentFormPageSettings: null,
  generatedQuestion: null,
  generatedQuestionOptions: null,
  answerType: null,
  answerTypeValue: null,
  options: null,
  correctIndex: null,
  conversationUuid: null,
  callToActionUrl: null,
  callToActionTarget: null,
  isRequired: false,
  projectMedia: [],
  project_type: null,
  isConversationCompleted: false,
  isRephrasedQuestion: false,
  clarityExplanation: null,
  userDetails: null,
  respondentInsights: null,
  isFromLogicFlow: false,
  isFromLogicTemplate: false,
  moveToFormPageId: null,
};

const formPageSlice = createSlice({
  name: 'formPage',
  initialState,
  reducers: {
    updateFormPages: (state, action: PayloadAction<any[]>) => {
      state.formPages = action.payload;
    },
    addMediaItem: (state, action: PayloadAction<MediaItem>) => {
      state.mediaItems.push(action.payload);
    },
    removeMediaItem: (state, action: PayloadAction<string>) => {
      const newMediaItems = state.mediaItems.filter(
        (item) => item.id != action.payload
      );
      state.mediaItems = newMediaItems;
    },
    setMediaItems: (state, action: PayloadAction<MediaItem[]>) => {
      state.mediaItems = action.payload;
    },
    // Add this new reducer
    resetFormPageState: (state) => {
      state.formPages = [];
      state.projectSettings = null;
      state.isLoading = false;
      state.error = null;
      state.pixabayImages = [];
      state.mediaItems = [];
      state.currentFormPageSettings = null;
      state.generatedQuestion = null;
      state.answerType = null;
      state.answerTypeValue = null;
      state.options = null;
      state.correctIndex = null;
      state.conversationUuid = null;
      state.clarityExplanation = null;
      state.moveToFormPageId = null;
      state.isFromLogicFlow = false;
      state.isFromLogicTemplate = false;
    },
    // Add a specific reducer for resetting only logic flow properties
    resetLogicFlowProperties: (state, action: PayloadAction<{isFromLogicFlow: boolean, moveToFormPageId: string | null}>) => {
      state.isFromLogicFlow = action.payload.isFromLogicFlow;
      state.moveToFormPageId = action.payload.moveToFormPageId;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFormPage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFormPage.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update or add the fetched form page
        const index = state.formPages.findIndex(
          (page) => page.id === action.payload.id
        );
        if (index !== -1) {
          state.formPages[index] = action.payload;
        } else {
          state.formPages.push(action.payload);
        }
      })
      .addCase(fetchFormPage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateFormPage.fulfilled, (state, action) => {
        const index = state.formPages.findIndex(
          (page) => page.id === action.payload.form_page.id
        );

        if (index !== -1) {
          state.formPages[index] = {
            ...state.formPages[index],
            title: action.payload.form_page.title || undefined,
            statement: action.payload.form_page.statement || undefined,
            call_to_action:
              action.payload.form_page.call_to_action || undefined,
          };
          // update call to action url and target if present
          if (action.payload.form_page.call_to_action_url) {
            state.formPages[index].call_to_action_url =
              action.payload.form_page.call_to_action_url;
          }

          if (action.payload.form_page.call_to_action_target) {
            state.formPages[index].call_to_action_target =
              action.payload.form_page.call_to_action_target;
          }
        } else {
          const newPage = {
            ...action.payload.form_page,
            title: action.payload.form_page.title || undefined,
            statement: action.payload.form_page.statement || undefined,
            call_to_action:
              action.payload.form_page.call_to_action || undefined,
          };
          // update call to action url and target if present
          if (action.payload.form_page.call_to_action_url) {
            newPage.call_to_action_url =
              action.payload.form_page.call_to_action_url;
          }

          if (action.payload.form_page.call_to_action_target) {
            newPage.call_to_action_target =
              action.payload.form_page.call_to_action_target;
          }
          state.formPages.push(newPage);
        }

        // Reorganize formPages by position
        state.formPages.sort((a, b) => a.position - b.position);
      })
      .addCase(fetchAllFormPages.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllFormPages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.formPages = action.payload; 
      })
      .addCase(fetchAllFormPages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchProjectSettings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchProjectSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.projectSettings = action.payload;
      })
      .addCase(fetchProjectSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProjectSettings.fulfilled, (state, action) => {
        state.projectSettings = action.payload;
      })
      .addCase(createFormPage.fulfilled, (state, action) => {
        const newPage = {
          ...action.payload,
          title: action.payload.title || undefined,
          statement: action.payload.statement || undefined,
          call_to_action: action.payload.call_to_action || undefined,
        };
        console.log("newPage", newPage);
        state.formPages.push(newPage);
        // Reorganize formPages by position
        state.formPages.sort((a, b) => a.position - b.position);
        console.log("state.formPages", state.formPages);
        
      })
      .addCase(deleteFormPage.fulfilled, (state, action) => {
        console.log("deleting form page", action.payload);
        state.formPages = state.formPages.filter(
          (page) => page.id != action.payload
        );
      })
      .addCase(searchPixabayImages.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(searchPixabayImages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pixabayImages = action.payload;
      })
      .addCase(searchPixabayImages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateFormPageFiles.fulfilled, (state, action) => {
        // Update the form page with the new file information
        const updatedPage = action.payload.file;
        const pageIndex = state.formPages.findIndex(
          (page) => page.id === updatedPage.form_page_id
        );
        if (pageIndex !== -1) {
          state.formPages[pageIndex] = {
            ...state.formPages[pageIndex],
            form_page_files: [action.payload.file], // Replace with new array containing only the new file
          };
        }
      })
      .addCase(removeBackgroundImage.fulfilled, (state, action) => {})
      .addCase(removeBackgroundImage.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(fetchFormPageSettings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFormPageSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentFormPageSettings = action.payload;
      })
      .addCase(fetchFormPageSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateFormPageSettings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateFormPageSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the currentFormPageSettings with the new settings
        state.currentFormPageSettings = action.payload.settings;
        // Also update the corresponding form page in the formPages array
        const updatedPage = state.formPages.find(
          (page) => page.id === action.payload.formPageId
        );
        if (updatedPage) {
          updatedPage.settings = action.payload.settings;
        }
      })
      .addCase(updateFormPageSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(generateFormPageQuestion.fulfilled, (state, action) => {
        console.log({payload: action.payload});
        if (action.payload.project_type === 'adaptive_forms') {
          // create new object and place object right before end page
          const newFormPages = [...state.formPages];
          // create new object and place object right before end page
          // extract files from welcome page
          const welcomePage = state.formPages.find(
            (page) => page.page_type === 'welcome'
          );
          const endPageIndex = newFormPages.findIndex(
            (page) => page.page_type === 'end'
          );
          const newPage = {
            position: action.payload.correct_index,
            page_type: 'prompt',
            title: action.payload.question,
            statement: action.payload.statement,
            settings: null,
            form_page_files: welcomePage?.form_page_files || [],
          };
          newFormPages.splice(endPageIndex, 0, newPage);
          state.formPages = newFormPages;
        }
        
        state.generateFormPageQuestionIsLoading = false;
        state.generatedQuestion = action.payload.question;
        state.generatedQuestionOptions = action.payload.options;
        state.answerType = action.payload.answer_type;
        state.answerTypeValue = action.payload.answer_type_value || null;
        state.options = action.payload.options;
        state.correctIndex = action.payload.correct_index;
        state.conversationUuid = action.payload.conversation_uuid;
        state.isRequired = action.payload.is_required;
        state.callToActionUrl = action.payload.call_to_action_url;
        state.callToActionTarget = action.payload.call_to_action_target;
        state.isConversationCompleted = action.payload.is_conversation_completed;
        state.isRephrasedQuestion = action.payload.rephrased_question || false;
        state.isFromLogicFlow = action.payload.is_from_logic_flow || false;
        state.isFromLogicTemplate = action.payload.is_from_logic_template || false;
        state.moveToFormPageId = action.payload.move_to_form_page_id || null;
        state.clarityExplanation = action.payload.clarity_explanation || null;
        state.formPageProjectSettings = action.payload.project_type==='guided_forms'
          ? action.payload.project_settings : null;
        
        // Add handling for respondent insights if available in the response
        if (action.payload.respondent_insights) {
          state.respondentInsights = action.payload.respondent_insights;
        }
        // console.log({state: state.respondentInsights})
        // console.log({action: action.payload.respondent_insights})
        // console.log({projectSettings: state.projectSettings})
        // Add user details if available
        if (action.payload.user_details) {
          state.userDetails = action.payload.user_details;
        }
        
        // Remove the conversation ID cookie if the conversation is completed
        if (action.payload.is_conversation_completed) {
          // Since this is outside React, we need to call the function directly
          // This is a side effect in a reducer, which isn't ideal Redux pattern,
          // but necessary in this case for the cookie management
          removeConversationIdCookie(action.payload.conversation_uuid);
        }
      })
      .addCase(generateFormPageQuestion.pending, (state) => {
        state.generateFormPageQuestionIsLoading = true;
      })
      .addCase(generateFormPageQuestion.rejected, (state, action) => {
        state.generateFormPageQuestionIsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchProjectMedia.fulfilled, (state, action) => {
        state.projectMedia = action.payload;
      })
      .addCase(uploadProjectMedia.fulfilled, (state, action) => {
        state.projectMedia.push(action.payload);
      })
      .addCase(deleteProjectMedia.fulfilled, (state, action) => {
        state.projectMedia = state.projectMedia.filter(
          (media) => media.id !== action.payload
        );
      });
  },
});

export const {
  addMediaItem,
  removeMediaItem,
  setMediaItems,
  updateFormPages,
  resetFormPageState,
  resetLogicFlowProperties,
} = formPageSlice.actions;

export default formPageSlice.reducer;
